import React from 'react'
import { Device } from '../context/DeviceContext'
import { ManualControls } from '../context/SystemContext'
import { Button } from '@/components/ui/button'

type PumpRole = { pump: number; role: string }

type PumpProps = {
    pump: Device
    manualControls: ManualControls[]
    setManualControls: React.Dispatch<React.SetStateAction<ManualControls[]>>
}

const doseInputClass =
    'w-24 rounded-md border border-zinc-600 bg-zinc-950 px-2 py-1.5 text-sm text-zinc-100 tabular-nums placeholder:text-zinc-600 focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500/40'

const Pump = ({ pump, manualControls, setManualControls }: PumpProps) => {
    const roles = (Array.isArray(pump.roles) ? pump.roles : []) as PumpRole[]

    const applyDose = (role: PumpRole) => {
        const el = document.getElementById(`manual-dose-${pump.device_id}-${role.pump}`) as HTMLInputElement | null
        const raw = el?.value ?? ''
        const dose = raw === '' ? 0 : Number(raw)
        if (Number.isNaN(dose)) return

        setManualControls((prev) => {
            const idx = prev.findIndex(
                (c) =>
                    c.device_id === pump.device_id &&
                    'pump' in c &&
                    (c as { device_type?: string }).device_type === 'pump' &&
                    (c as { pump: number }).pump === role.pump
            )
            const entry = {
                device_id: pump.device_id,
                device_type: 'pump',
                pump: role.pump,
                dose,
            } as ManualControls
            if (idx === -1) return [...prev, entry]
            const next = [...prev]
            next[idx] = { ...(next[idx] as object), ...entry } as ManualControls
            return next
        })
    }

    return (
        <div className="rounded-lg border border-zinc-700/90 bg-zinc-950/55 p-4 shadow-sm">
            <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2 border-b border-zinc-800 pb-2">
                <h4 className="text-sm font-semibold text-zinc-100">{pump.name}</h4>
                <span className="text-xs font-medium uppercase tracking-wide text-zinc-500">Pump</span>
            </div>
            {roles.length === 0 ? (
                <p className="text-sm text-zinc-500">No pump roles configured for this device.</p>
            ) : (
                <ul className="space-y-4">
                    {roles.map((role) => (
                        <li
                            key={`${pump.device_id}-${role.pump}`}
                            className="flex flex-col gap-3 border-b border-zinc-800/80 pb-4 last:border-0 last:pb-0 sm:flex-row sm:items-end sm:justify-between"
                        >
                            <div className="min-w-0 flex-1">
                                <p className="text-xs font-medium uppercase tracking-wide text-zinc-500">Role</p>
                                <p className="text-sm text-zinc-200">{role.role}</p>
                            </div>
                            <div className="flex flex-wrap items-center gap-2">
                                <label className="sr-only" htmlFor={`manual-dose-${pump.device_id}-${role.pump}`}>
                                    Dose for {role.role} (ml)
                                </label>
                                <div className="flex items-center gap-1.5">
                                    <input
                                        id={`manual-dose-${pump.device_id}-${role.pump}`}
                                        type="number"
                                        min={0}
                                        step={0.1}
                                        defaultValue={
                                            (
                                                manualControls.find(
                                                    (c) =>
                                                        'pump' in c &&
                                                        c.device_id === pump.device_id &&
                                                        (c as { pump: number }).pump === role.pump
                                                ) as { dose?: number } | undefined
                                            )?.dose ?? ''
                                        }
                                        className={doseInputClass}
                                        placeholder="ml"
                                    />
                                    <span className="text-xs text-zinc-500">ml</span>
                                </div>
                                <Button
                                    type="button"
                                    size="sm"
                                    className="bg-emerald-600 text-white hover:bg-emerald-500"
                                    onClick={() => applyDose(role)}
                                >
                                    Set dose
                                </Button>
                            </div>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    )
}

export default Pump
